import { Component, effect, inject, signal, DestroyRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, SlicePipe } from '@angular/common';
import { CaseService, CaseResult } from '../../services/case-service/case.service';
import { DataTable, DataTableColumn } from '../../shared-components/data-table/data-table';
import { Toast } from '../../shared-components/toast/toast';
import { ActionService } from '../../services/action-service/action.service';
import { formatDate } from '../../shared-components/utils/date-format.util';

@Component({
  selector: 'app-search-results',
  imports: [CommonModule, DataTable, SlicePipe, Toast],
  templateUrl: `./search-results.html`,
  styleUrls: [`./search-results.scss`]
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  private readonly caseService = inject(CaseService);
  protected readonly actionService = inject(ActionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly searchResults = this.caseService.searchResults;
  protected readonly selectedCase = this.caseService.selectedCase;
  protected readonly activeModalCase = signal<CaseResult | null>(null);

  protected readonly expandedCases = this.caseService.expandedNoteCases;

  protected readonly toast = this.caseService.toast;
  protected readonly generatedLetter = this.caseService.generatedLetter;
  
  protected readonly totalRecords = this.caseService.totalRecords;
  protected readonly currentPage = this.caseService.currentPage;

  // FIX: Track pending clipboard operations to cancel them on destroy
  private pendingClipboardOperations: AbortController[] = [];

  // Data-table config
  protected readonly columns: DataTableColumn<CaseResult>[] = [
    { key: 'caseNo', header: 'Case #', width: '10%', minWidth: '80px', sortable: true, bold: true },
    { key: 'program', header: 'Program', width: '18%', minWidth: '130px', sortable: false },
    { key: 'address', header: 'Address', width: '20%', minWidth: '160px', sortable: false },
    { key: 'openDate', header: 'Open Date', width: '12%', minWidth: '90px', sortable: true, formatter: (r) => r.openDate ? formatDate(r.openDate) : '' },
    { key: 'closeDate', header: 'Close Date', width: '12%', minWidth: '90px', sortable: true, formatter: (r) => r.closeDate ? formatDate(r.closeDate) : '' },
    { key: 'inspector', header: 'Inspector', width: '12%', minWidth: '100px', sortable: false, formatter: (r) => r.inspector },
    { key: 'secondInspector', header: '2nd Inspector', width: '12%', minWidth: '100px', sortable: false, formatter: (r) => r.secondInspector },
  ];

  protected readonly filterKeys: (keyof CaseResult)[] = ['caseNo', 'program', 'address', 'inspector', 'secondInspector', 'openDate', 'closeDate'];

  protected trackByCaseNo = (item: CaseResult) => item.caseNo;
  protected isRowSelected = (item: CaseResult) => this.selectedCase()?.caseNo === item.caseNo;

  ngOnInit(): void {
    // Component initialization if needed
  }

  // FIX: Implement OnDestroy to clean up resources
  ngOnDestroy(): void {
    // Abort all pending clipboard operations
    this.pendingClipboardOperations.forEach(controller => controller.abort());
    this.pendingClipboardOperations = [];

    // Clear signal references to allow garbage collection
    this.activeModalCase.set(null);
    this.expandedCases.set(new Set());
  }

  toggleNotes(caseNo: string, event: Event) {
    event.stopPropagation();
    const current = new Set(this.expandedCases());
    if (current.has(caseNo)) {
      current.delete(caseNo);
    } else {
      current.add(caseNo);
    }
    this.expandedCases.set(current);
  }

  selectCase(item: CaseResult) {
    if (this.selectedCase()?.caseNo === item.caseNo) {
      this.caseService.selectedCase.set(null);
    } else {
      this.caseService.selectedCase.set(item);
    }
    this.actionService.reset();
  }

  openNotesModal(item: CaseResult, event: Event) {
    event.stopPropagation();
    this.activeModalCase.set(item);
  }

  closeNotesModal() {
    this.activeModalCase.set(null);
  }

  copyPath(path: string) {
    // FIX: Use AbortController to handle component destruction
    const controller = new AbortController();
    this.pendingClipboardOperations.push(controller);

    navigator.clipboard.writeText(path).then(() => {
      // Check if component is still alive and operation wasn't aborted
      if (!controller.signal.aborted) {
        this.caseService.showToast('Network path copied to clipboard!', 'success');
      }
      // Remove completed operation from tracking
      this.pendingClipboardOperations = this.pendingClipboardOperations.filter(op => op !== controller);
    }).catch((error) => {
      // Check if operation wasn't aborted (aborted operations throw DOMException)
      if (!controller.signal.aborted) {
        console.error('Clipboard error:', error);
        this.caseService.showToast('Failed to copy network path.', 'error');
      }
      // Remove operation from tracking
      this.pendingClipboardOperations = this.pendingClipboardOperations.filter(op => op !== controller);
    });
  }

  clearToast() {
    this.caseService.clearToast();
  }

  onPageOrSizeChange(event: { page: number; pageSize: number }) {
    this.caseService.changePage(event.page, event.pageSize);
  }
}